import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import api from '../api/api';
import { useAuth } from '../context/AuthContext';
import { useUpload } from '../context/UploadContext';
import { useDropzone } from 'react-dropzone';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { doc, getDoc, onSnapshot, updateDoc, serverTimestamp, deleteField } from 'firebase/firestore';
import { factReviewArgs, factUpdateArgs, markAllVerifiedArgs } from './editcase/factPaths';
import { confirmAllArgs, eventReviewArgs, reviewEntry } from './editcase/timelinePaths';
import { reviewerOf, reviewStamp } from './editcase/reviewIdentity';
import {
    markAllRedFlagsReviewed,
    refineCandidate,
    reviewCandidate,
    reviewRedFlag,
} from './editcase/reviewUpdates';
import { allActiveEventsReviewed, activeReviewProgress } from '../utils/timelineReview';
import { db } from '../firebase/firebase';
import { resolveCaseOwner } from '../utils/caseOwner';
import { notify } from '../utils/notifications';
import { isBillingBlockedError } from '../utils/billingEvents';
import { ACCEPT, CASE_FULL_MESSAGE, MAX_FILES_PER_CASE, MAX_UPLOAD_BYTES, rejectionMessages } from '../utils/uploadLimits';
import groupFacts from '../utils/groupFacts';
import useWorkflowStage from '../hooks/useWorkflowStage';
import { useUpdateAdmissionStatus } from '../hooks/useUpdateAdmissionStatus';
import WorkflowStepper from './editcase/WorkflowStepper';
import RedFlagsAlert from './editcase/RedFlagsAlert';
import RiskScoreBanner from './editcase/RiskScoreBanner';
import IdentityMismatchBanner from './editcase/IdentityMismatchBanner';
import ProcessingWarnings from './editcase/ProcessingWarnings';
import FactsPanel from './editcase/FactsPanel';
import ResultsTabs, { tabId, panelId } from './editcase/ResultsTabs';
import TimelineTab from './editcase/TimelineTab';
import ActiveCarePlanCard from './editcase/ActiveCarePlanCard';
import UploadProgressPanel from './editcase/UploadProgressPanel';
import VerificationBar from './editcase/VerificationSidebar';
import HPIContent from './editcase/HPIContent';
import DiagnosisContent from './editcase/DiagnosisContent';
import { getDefaultTabKey } from './editcase/reviewTabs';
import DiagnosisCodesPanel from './editcase/DiagnosisCodesPanel';
import CodeSearchModal from './editcase/CodeSearchModal';
import { toIsoDate } from '../utils/isoDate';
import ConfirmDialog from './ConfirmDialog';
import { buildPhysicianQuery } from './editcase/physicianQuery';
import { copyWithProvenance } from '../utils/provenance';
import '../styles/EditCase.css';

function EditCase() {
    const { caseId } = useParams();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [answer, setAnswer] = useState("");
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [diagnosis, setDiagnosis] = useState("");
    const { user, userProfile, isAdmin, isFacilityAdmin } = useAuth();
    // B4: a colleague's case lives under its owner's path. ?owner= comes from
    // the case list (caseHref); an absent or malformed value means "my own
    // case", so every pre-B4 link still resolves. Backend posts carry
    // owner_uid only when it differs from the caller; the server enforces
    // same-facility membership.
    const ownerUid = resolveCaseOwner(searchParams, user?.uid);
    const isOwner = ownerUid === user?.uid;
    // C5: /case/remove-file admits the owner or a facility admin of the
    // case's facility. Upload stays owner-only (see uploadSection).
    const canRemoveFiles = isOwner || Boolean(isFacilityAdmin);
    // F2: /case/retry-failed admits the same callers as remove-file.
    const canRetry = isOwner || Boolean(isFacilityAdmin);
    // A cross-facility URL, or a membership that ended, surfaces as a
    // Firestore permission-denied or a 403 case_access_denied from the API.
    // Either replaces the page with a denied panel instead of a redirect.
    const [accessDenied, setAccessDenied] = useState(false);
    const isDenied = (err) =>
        err?.code === 'permission-denied' || err?.response?.data?.code === 'case_access_denied';
    const caseRef = useMemo(
        () => (ownerUid && caseId ? doc(db, 'users', ownerUid, 'cases', caseId) : null),
        [ownerUid, caseId]
    );
    const uploadCtx = useUpload();
    const [activeTab, setActiveTab] = useState("hpi");
    const [structuredInfo, setStructuredInfo] = useState(null);
    const [editableAnswers, setEditableAnswers] = useState({});
    const [copySuccess, setCopySuccess] = useState("");
    const [copyDiagnosisSuccess, setCopyDiagnosisSuccess] = useState("");
    const [isGenerating, setIsGenerating] = useState(false);
    const [generatingStartTime, setGeneratingStartTime] = useState(null);
    const [processingTimedOut, setProcessingTimedOut] = useState(false);
    const [uploadedFiles, setUploadedFiles] = useState([]);
    // File awaiting the remove confirm dialog, and whether its post is in flight
    const [pendingRemoval, setPendingRemoval] = useState(null);
    const [removingFile, setRemovingFile] = useState(false);
    const [admissionStatus, setAdmissionStatus] = useState("In progress");
    const [isArchived, setIsArchived] = useState(false);
    const [extractionProgress, setExtractionProgress] = useState(null);
    const [redFlags, setRedFlags] = useState(null);
    const [diagnosisCodes, setDiagnosisCodes] = useState(null);
    const [copyDxCodesSuccess, setCopyDxCodesSuccess] = useState(false);
    // Code search/refine modal target:
    // null | {mode:'refine', code, index} | {mode:'lookup'}
    const [codeSearchTarget, setCodeSearchTarget] = useState(null);
    // code -> lookup client_id for this session's lookup evidence checks
    const [lookupClientIds, setLookupClientIds] = useState({});
    // client_ids whose evidence search has run out its client-side clock
    const [evidenceTimeouts, setEvidenceTimeouts] = useState({});
    const evidenceTimersRef = useRef({});
    const [copiedQueryIndex, setCopiedQueryIndex] = useState(null);
    const [riskScore, setRiskScore] = useState(null);
    const [recommendation, setRecommendation] = useState(null);
    // F2: structured entries {code, step, message, at, resolved} written by
    // the pipeline for each non-fatal step failure; old cases may hold plain
    // strings. caseStatus mirrors the status object so the banner can hide
    // while a run or a retry is in flight.
    const [processingWarnings, setProcessingWarnings] = useState([]);
    const [caseStatus, setCaseStatus] = useState(null);
    const [facilityType, setFacilityType] = useState(null);
    const [timeline, setTimeline] = useState([]);
    const [timelineReview, setTimelineReview] = useState({});
    const [codingStatus, setCodingStatus] = useState(null);
    // Case creation date (YYYY-MM-DD): the ICD endpoints pick the
    // fiscal-year table for this date, as the backend pipeline does.
    const [caseAsOf, setCaseAsOf] = useState(undefined);
    const [resultsTab, setResultsTab] = useState('facts');
    const [showReSuggestConfirm, setShowReSuggestConfirm] = useState(false);

    const [generatedDocuments, setGeneratedDocuments] = useState(null);
    const [documentOrder, setDocumentOrder] = useState(null);
    // Which generated document tab just copied (drives its button's "Copied!" state)
    const [copiedDocName, setCopiedDocName] = useState(null);

    // F6 provenance: when each draft was generated, and who signed the
    // review off. Both feed the footer on every copy and under every
    // rendered document. review_signoff is {name, uid, at}.
    const [reviewSignoff, setReviewSignoff] = useState(null);
    const [hpiGeneratedAt, setHpiGeneratedAt] = useState(null);
    const [diagnosisGeneratedAt, setDiagnosisGeneratedAt] = useState(null);
    const [lastModifiedAt, setLastModifiedAt] = useState(null);
    // The sign-off effect below must not act before the first snapshot: an
    // empty case would read as "gate shut, clear the sign-off". This is
    // state, not a ref, so the effect always runs on the first snapshot and
    // takes its baseline from it.
    const [caseLoaded, setCaseLoaded] = useState(false);
    // The gate value at the previous run of the sign-off effect (null = no
    // baseline yet), so only a transition seen on this page records a
    // sign-off.
    const prevGateRef = useRef(null);
    // Intent of the sign-off write already sent (true = recorded, false =
    // cleared, null = in sync), so one gate change makes one write.
    const signoffIntentRef = useRef(null);

    const [collapsedGroups, setCollapsedGroups] = useState({});
    const [expandedCitations, setExpandedCitations] = useState({});

    const [documentsStale, setDocumentsStale] = useState(false);
    const [resultsStale, setResultsStale] = useState(false);
    const [expandedFileNames, setExpandedFileNames] = useState(new Set());
    const [showReExtractConfirm, setShowReExtractConfirm] = useState(false);
    const [showReGenerateConfirm, setShowReGenerateConfirm] = useState(false);
    // Tracks whether a fresh extraction is in flight (so we know when to clear resultsStale)
    const pendingExtraction = useRef(false);
    // Fields whose last answer save failed; handleAnswerEdit toasts once per field.
    const failedEditFieldsRef = useRef(new Set());
    // Last toasted processing-error message; lets the snapshot handler toast
    // once per transition instead of once per snapshot.
    const lastProcessingErrorRef = useRef(null);
    // Last answer per fact that Firestore holds (a snapshot or a successful
    // save); a failed edit reverts to it, never to an unsaved keystroke.
    const confirmedAnswersRef = useRef({});
    // Per-field answer save counters: the last one issued, and the highest
    // one that succeeded. An older save settling late cannot move the
    // rollback target back past a newer confirmed one.
    const answerSaveSeqRef = useRef({});
    const answerConfirmedSeqRef = useRef({});
    // Timeline review entry per event id that Firestore holds (undefined =
    // unreviewed), and a per-event operation counter. A failed review
    // restores only its own events, and only those no newer review touched.
    const confirmedTimelineRef = useRef({});
    const timelineOpRef = useRef({});
    // The load effect and the first snapshot can both give up on a case (a
    // load error or a missing document); toast and redirect only once.
    const leftCaseRef = useRef(false);
    // A snapshot error toasts once until a later snapshot succeeds.
    const connectionLostToastedRef = useRef(false);
    // Bumped on every case change. A write captures it before its await and
    // skips all ref and state changes if the user has moved to another case
    // by the time it settles (EditCase stays mounted across /case/:caseId).
    const caseGenerationRef = useRef(0);
    const stillOnCase = (generation) => caseGenerationRef.current === generation;
    // Guards the one-time UploadContext.clear() below so it can't loop.
    const uploadCtxCleared = useRef(false);
    // Re-arm the guard whenever the viewed case changes, so clear() can
    // still fire the *next* time this same case's extraction appears.
    useEffect(() => {
        caseGenerationRef.current += 1;
        uploadCtxCleared.current = false;
        failedEditFieldsRef.current = new Set();
        lastProcessingErrorRef.current = null;
        confirmedAnswersRef.current = {};
        answerSaveSeqRef.current = {};
        answerConfirmedSeqRef.current = {};
        confirmedTimelineRef.current = {};
        timelineOpRef.current = {};
        leftCaseRef.current = false;
        connectionLostToastedRef.current = false;
        setCaseLoaded(false);
        prevGateRef.current = null;
        signoffIntentRef.current = null;
    }, [caseId]);

    // Once extraction is underway (progress or results present) FOR THE
    // CASE CURRENTLY BEING VIEWED, the upload panel's job is done — clear
    // UploadContext so it stops rendering and the case's own
    // Firestore-backed file list takes over.
    //
    // Critically gated on uploadCtx.caseId === caseId: UploadContext is a
    // single, app-wide upload engine, so a background upload for case A can
    // still be in flight while the user navigates to and views an unrelated
    // case B that already has extraction results. Without this check,
    // opening B would clear() UploadContext — aborting A's in-flight PUTs
    // and silently killing that upload.
    useEffect(() => {
        if (uploadCtxCleared.current) return;
        if (uploadCtx.caseId !== caseId) return;
        if (extractionProgress || structuredInfo) {
            uploadCtxCleared.current = true;
            uploadCtx.clear();
        }
    }, [caseId, extractionProgress, structuredInfo, uploadCtx]);

    // Data-driven stage (furthest progress reached)
    const { stage: workflowStage } = useWorkflowStage({
        structuredInfo,
        extractionProgress,
        hpiText: answer,
        diagnosis,
        generatedDocuments,
    });

    // User-controlled viewing stage (null = follow data-driven stage)
    const [viewingStage, setViewingStage] = useState(null);
    const activeStage = viewingStage ?? workflowStage;

    // Home-health diagnosis codes are computed at upload-time processing and
    // reviewed in their own Coding step between Review & Approve and
    // Documents. The step exists and is clickable as soon as codes do, before
    // fact verification or document generation. The data-driven workflowStage
    // still decides where the user lands by default, so fact review stays the
    // current stage until generation.
    const showCodingStep = facilityType === 'home_health' && (!!diagnosisCodes || !!codingStatus);

    // Per-step reachability for the stepper. The ordinal stages unlock with
    // data-driven progress (as before); Coding unlocks with the codes
    // themselves, independent of stage progress.
    const reachableStages = useMemo(() => {
        const reachable = new Set(['upload']);
        if (workflowStage === 'results' || workflowStage === 'documents') {
            reachable.add('results');
        }
        if (workflowStage === 'documents') {
            reachable.add('documents');
        }
        if (showCodingStep) {
            reachable.add('coding');
        }
        return reachable;
    }, [workflowStage, showCodingStep]);

    // When the data-driven stage advances, reset viewing override so user sees the new stage
    const prevWorkflowStageRef = useRef(null);
    useEffect(() => {
        if (prevWorkflowStageRef.current !== null && workflowStage !== prevWorkflowStageRef.current) {
            setViewingStage(null);
        }
        prevWorkflowStageRef.current = workflowStage;
    }, [workflowStage]);

    const handleStageClick = (stageKey) => {
        if (stageKey === workflowStage) {
            // Clicking the current progress stage clears the override
            setViewingStage(null);
        } else {
            setViewingStage(stageKey);
        }
    };

    // "Go to files" on the identity banner (F1): the file list lives in the
    // upload stage, so switch there and scroll to it once it has rendered.
    const scrollToFilesPending = useRef(false);
    const handleGoToFiles = () => {
        scrollToFilesPending.current = true;
        setViewingStage(workflowStage === 'upload' ? null : 'upload');
    };
    useEffect(() => {
        if (!scrollToFilesPending.current || activeStage !== 'upload') return;
        scrollToFilesPending.current = false;
        document.getElementById('case-files')?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
    }, [activeStage]);

    // Sort document names by facility config order (Firestore maps lose insertion order)
    const sortedDocNames = useMemo(() => {
        if (!generatedDocuments) return [];
        const keys = Object.keys(generatedDocuments);
        if (!documentOrder) return keys;
        return keys.sort((a, b) => {
            const ai = documentOrder.indexOf(a);
            const bi = documentOrder.indexOf(b);
            return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
        });
    }, [generatedDocuments, documentOrder]);

    // When generatedDocuments arrives, initialize activeTab to the default
    // document. Every Firestore snapshot gives sortedDocNames a fresh array
    // identity, so this effect re-runs on every write to the case; it must
    // leave activeTab alone whenever the current tab is still valid, or any
    // save (fact edit, admission status change) kicks the user back to the
    // default tab.
    useEffect(() => {
        if (sortedDocNames.length === 0) return;
        setActiveTab(prev => {
            if (sortedDocNames.includes(prev)) return prev;
            // Persona-aware default. Role plumbing is deferred (see plan Task
            // 13); callers pass null until facility membership/role data is
            // exposed.
            const preferred = getDefaultTabKey(facilityType, null);
            return sortedDocNames.includes(preferred) ? preferred : sortedDocNames[0];
        });
    }, [sortedDocNames, facilityType]);

    // Processing timeout configuration (10 minutes)
    const PROCESSING_TIMEOUT_MS = 10 * 60 * 1000;

    const toggleGroup = (groupName) => {
        setCollapsedGroups(prev => ({ ...prev, [groupName]: !prev[groupName] }));
    };

    const toggleCitation = (fieldKey) => {
        setExpandedCitations(prev => ({ ...prev, [fieldKey]: !prev[fieldKey] }));
    };

    const groupedFacts = useMemo(() => groupFacts(structuredInfo), [structuredInfo]);
    const prevGroupedFactsRef = useRef([]);

    // Auto-collapse groups when all their facts are verified.
    // On initial load, collapse already-verified groups immediately.
    // On subsequent updates, collapse newly-completed groups after 300ms,
    // unless ALL groups completed at once ("Mark All Correct").
    useEffect(() => {
        if (groupedFacts.length === 0) return;

        const prevGroups = prevGroupedFactsRef.current;
        const isInitialLoad = prevGroups.length === 0;
        prevGroupedFactsRef.current = groupedFacts;

        if (isInitialLoad) {
            // Collapse any groups that are already fully verified when case loads
            const toCollapse = {};
            for (const group of groupedFacts) {
                if (group.verifiedCount === group.totalCount && group.totalCount > 0) {
                    toCollapse[group.name] = true;
                }
            }
            if (Object.keys(toCollapse).length > 0) {
                setCollapsedGroups(prev => ({ ...prev, ...toCollapse }));
            }
            return;
        }

        // Skip auto-collapse when ALL groups are complete (e.g. "Mark All Correct")
        const allComplete = groupedFacts.every(g => g.verifiedCount === g.totalCount && g.totalCount > 0);
        if (allComplete) return;

        const timeouts = [];
        for (const group of groupedFacts) {
            if (group.verifiedCount === group.totalCount && group.totalCount > 0 && !collapsedGroups[group.name]) {
                const id = setTimeout(() => {
                    setCollapsedGroups(prev => {
                        if (prev[group.name]) return prev;
                        return { ...prev, [group.name]: true };
                    });
                }, 300);
                timeouts.push(id);
            }
        }
        return () => timeouts.forEach(clearTimeout);
    }, [groupedFacts]); // eslint-disable-line react-hooks/exhaustive-deps

    // Load case data if editing an existing case
    // Toast and return to the case list, once per case.
    const leaveCase = useCallback((message) => {
        if (leftCaseRef.current) return;
        leftCaseRef.current = true;
        notify.error(message);
        navigate('/cases');
    }, [navigate]);
    const handleMissingCase = useCallback(
        () => leaveCase('This case no longer exists.'),
        [leaveCase],
    );

    const rememberConfirmedAnswers = (info) => {
        Object.entries(info || {}).forEach(([key, value]) => {
            confirmedAnswersRef.current[key] = value?.answer;
        });
    };

    useEffect(() => {
        const loadCaseData = async () => {
            // Reset state for both new and existing cases
            setAnswer("");
            setDiagnosis("");
            setSelectedFiles([]);
            setStructuredInfo(null);
            setEditableAnswers({});
            setCopySuccess("");
            setCopyDiagnosisSuccess("");

            // If caseId is undefined or 'new', or no user, we're done
            if (!caseId || caseId === 'new' || !user?.uid || !caseRef) {
                return;
            }

            // Only try to load existing case data if we have a valid caseId.
            // A load that settles after a switch to another case is dropped.
            const generation = caseGenerationRef.current;
            try {
                const caseDoc = await getDoc(caseRef);
                if (!stillOnCase(generation)) return;

                if (caseDoc.exists()) {
                    const caseData = caseDoc.data();
                    // Set the structured info from the stored data
                    if (caseData.structuredInfo) {
                        rememberConfirmedAnswers(caseData.structuredInfo);
                        setStructuredInfo(caseData.structuredInfo);
                    }
                } else {
                    console.error('Case not found');
                    handleMissingCase();
                }
            } catch (error) {
                if (!stillOnCase(generation)) return;
                if (isDenied(error)) {
                    setAccessDenied(true);
                    return;
                }
                console.error('Error loading case:', error);
                leaveCase('Could not load this case.');
            }
        };

        loadCaseData();
    }, [caseId, user?.uid, caseRef, handleMissingCase, leaveCase]);

    useEffect(() => {
        // Subscribe to real-time updates for the case
        if (!caseId || caseId === 'new' || !user?.uid || !caseRef) {
            return;
        }

        const unsubscribe = onSnapshot(caseRef, (doc) => {
            connectionLostToastedRef.current = false;
            if (doc.exists()) {
                const caseData = doc.data();
                // A latency-compensated echo of this user's own write is not
                // server-confirmed, so it never becomes a rollback target.
                const serverConfirmed = doc.metadata?.hasPendingWrites !== true;
                // Update the UI with the latest data
                if (caseData.structuredInfo) {
                    if (serverConfirmed) rememberConfirmedAnswers(caseData.structuredInfo);
                    setStructuredInfo(caseData.structuredInfo);
                }
                if (caseData.hpi) {
                    setAnswer(prev => {
                        // If HPI content changed (fresh generation), clear stale flag
                        if (caseData.hpi !== prev) setDocumentsStale(false);
                        return caseData.hpi;
                    });
                }
                if (caseData.diagnosis) {
                    setDiagnosis(caseData.diagnosis);
                }
                if (caseData.generated_documents) {
                    setGeneratedDocuments(caseData.generated_documents);
                }
                if (caseData.document_order) {
                    setDocumentOrder(caseData.document_order);
                }
                if (caseData.files) {
                    setUploadedFiles(caseData.files);
                }
                if (caseData.admission_status) {
                    setAdmissionStatus(caseData.admission_status);
                }
                setIsArchived(caseData.archived === true);
                // Update extraction progress (for progressive field extraction)
                if (caseData.extraction_progress) {
                    setExtractionProgress(caseData.extraction_progress);
                } else {
                    setExtractionProgress(null);
                }
                // Update red flags
                if (caseData.redFlags) {
                    setRedFlags(caseData.redFlags);
                }
                // Update diagnosis codes (home_health cases only)
                setDiagnosisCodes(caseData.diagnosisCodes ?? null);
                setRiskScore(caseData.risk_score ?? null);
                setRecommendation(caseData.recommendation ?? null);
                setProcessingWarnings(caseData.processing_warnings ?? []);
                setFacilityType(caseData.facility_type ?? null);
                setTimeline(caseData.timeline || []);
                if (serverConfirmed) {
                    confirmedTimelineRef.current = { ...(caseData.timeline_review || {}) };
                }
                setTimelineReview(caseData.timeline_review || {});
                setCodingStatus(caseData.codingStatus ?? null);
                setCaseAsOf(toIsoDate(caseData.createdAt));
                // F6 provenance sources
                setReviewSignoff(caseData.review_signoff ?? null);
                setHpiGeneratedAt(caseData.hpi_generated_at ?? null);
                setDiagnosisGeneratedAt(caseData.diagnosis_generated_at ?? null);
                setLastModifiedAt(caseData.last_modified_date ?? null);
                setCaseLoaded(true);
                // Update case completion status

                // Update generation status: only turn OFF generating when
                // status explicitly transitions to success/error (avoids
                // flicker from snapshots that arrive before backend sets 'working')
                const statusVal = caseData.status;
                setCaseStatus(statusVal && typeof statusVal === 'object' ? statusVal : null);
                const wasGenerating = isGenerating;
                const isWorking = !!statusVal && typeof statusVal === 'object' && statusVal.condition === 'working';
                // complete_with_warnings (F2) is terminal: the run finished
                // and the banner below names the steps that did not.
                const isDone = !!statusVal && typeof statusVal === 'object' &&
                    (statusVal.condition === 'success' ||
                        statusVal.condition === 'error' ||
                        statusVal.condition === 'complete_with_warnings');

                if (isWorking && !wasGenerating) {
                    setIsGenerating(true);
                    setGeneratingStartTime(Date.now());
                    setProcessingTimedOut(false);
                } else if (isDone && wasGenerating) {
                    setIsGenerating(false);
                    setGeneratingStartTime(null);
                    setProcessingTimedOut(false);
                    // If a re-extraction was pending, clear staleness now that it completed
                    if (pendingExtraction.current) {
                        pendingExtraction.current = false;
                        setResultsStale(false);
                        setDocumentsStale(false);
                    }
                }

                // Toast the processing error once per transition, not per
                // snapshot: the ref survives this effect re-subscribing when
                // isGenerating flips.
                if (statusVal && statusVal.condition === 'error') {
                    const key = String(statusVal.message ?? '');
                    if (lastProcessingErrorRef.current !== key) {
                        lastProcessingErrorRef.current = key;
                        console.error('Processing error:', statusVal.message);
                        const message = String(statusVal.message ?? '').trim();
                        notify.error(message ? `Processing failed: ${statusVal.message}` : 'Processing failed.');
                    }
                } else {
                    lastProcessingErrorRef.current = null;
                }
            } else {
                handleMissingCase();
            }
        }, (err) => {
            if (isDenied(err)) setAccessDenied(true);
            else {
                console.error('Case snapshot error:', err);
                if (!connectionLostToastedRef.current) {
                    connectionLostToastedRef.current = true;
                    notify.error('Lost the connection to this case. Reload the page.');
                }
            }
        });

        // Cleanup subscription on unmount
        return () => unsubscribe();
    }, [caseId, user?.uid, caseRef, isGenerating, navigate, handleMissingCase]);

    // Timeout handling for stuck processing
    useEffect(() => {
        if (!isGenerating || !generatingStartTime) {
            return;
        }

        const checkTimeout = () => {
            const elapsed = Date.now() - generatingStartTime;
            if (elapsed >= PROCESSING_TIMEOUT_MS) {
                console.warn('Processing timeout detected after', Math.round(elapsed / 1000), 'seconds');
                setProcessingTimedOut(true);
            }
        };

        // Check immediately in case we're already past timeout
        checkTimeout();

        // Set up interval to check periodically
        const intervalId = setInterval(checkTimeout, 30000); // Check every 30 seconds

        return () => clearInterval(intervalId);
    }, [isGenerating, generatingStartTime, PROCESSING_TIMEOUT_MS]);

    const onDrop = useCallback((acceptedFiles) => {
        setSelectedFiles(acceptedFiles);
    }, []);

    // C11: one toast per distinct reason, so a drop of ten oversized files
    // does not stack ten identical toasts.
    const onDropRejected = useCallback((fileRejections) => {
        rejectionMessages(fileRejections).forEach((message) => notify.error(message));
    }, []);

    // Files the case can still take. react-dropzone reads maxFiles 0 as
    // unlimited, so a full case disables the dropzone instead and keeps
    // maxFiles at the case limit.
    const remainingFileSlots = Math.max(MAX_FILES_PER_CASE - uploadedFiles.length, 0);
    const caseIsFull = remainingFileSlots === 0;

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        onDropRejected,
        accept: ACCEPT,
        maxSize: MAX_UPLOAD_BYTES,
        maxFiles: caseIsFull ? MAX_FILES_PER_CASE : remainingFileSlots,
        multiple: true,
        disabled: caseIsFull,
    });

    const handleFileUpload = (e) => {
        e.preventDefault();
        if (!selectedFiles.length) return;

        // Single upload path: hand off to UploadContext so these files get
        // the same progress/stall/retry handling as a brand-new case's
        // uploads. autoProcess:false because this flow always ends with the
        // user manually clicking Extract/Re-extract — never an automatic
        // process-case call (unlike NewCase's initial upload).
        uploadCtx.addFilesToCase(caseId, selectedFiles, { autoProcess: false });

        // Downstream stages are now stale — source documents changed. Set
        // optimistically since addFilesToCase uploads in the background and
        // doesn't resolve per-file completion back to the caller.
        setResultsStale(true);
        setDocumentsStale(true);

        setSelectedFiles([]);
    };

    // C5: the rules lock the case's `files` array, so removal is a backend
    // call. Clicking Remove opens the confirm dialog; confirmRemoveFile posts.
    const handleDeleteFile = (file) => {
        if (!user?.uid || !caseId) return;
        setPendingRemoval(file);
    };

    const cancelRemoveFile = useCallback(() => {
        if (removingFile) return;
        setPendingRemoval(null);
    }, [removingFile]);

    const confirmRemoveFile = async () => {
        const file = pendingRemoval;
        if (!file || removingFile) return;
        setRemovingFile(true);
        try {
            const formData = new FormData();
            formData.append('case_id', caseId);
            formData.append('file_path', file.path);
            if (!isOwner) formData.append('owner_uid', ownerUid);

            await api.post('/case/remove-file', formData);

            setUploadedFiles(prev => prev.filter(f => f.path !== file.path));
            // Downstream stages are now stale: the source documents changed
            setResultsStale(true);
            setDocumentsStale(true);
        } catch (error) {
            const code = error?.response?.data?.code;
            if (isDenied(error)) {
                notify.error('You do not have access to this case.');
            } else if (error?.response?.status === 404 && code === 'file_not_found') {
                // The entry is already gone server-side; mirror the document.
                try {
                    const caseDoc = await getDoc(caseRef);
                    setUploadedFiles(caseDoc.data()?.files || []);
                } catch (readError) {
                    console.error('Error refreshing file list:', readError);
                }
            } else {
                console.error('Error removing file:', error);
                notify.error('Failed to remove file');
            }
        } finally {
            setRemovingFile(false);
            setPendingRemoval(null);
        }
    };

    const handleProcessCase = async ({ confirm = false } = {}) => {
        if (!user?.uid || !caseId || isArchived) return;

        // If there are existing results, confirm before wiping them
        const hasExistingResults = !!(structuredInfo || extractionProgress);
        if (hasExistingResults && !confirm) {
            setShowReExtractConfirm(true);
            return;
        }
        setShowReExtractConfirm(false);

        // Clear existing extraction results and downstream content locally
        setStructuredInfo(null);
        setExtractionProgress(null);
        setEditableAnswers({});
        setRedFlags(null);
        setDiagnosisCodes(null);
        setTimeline([]);
        setTimelineReview({});
        setCodingStatus(null);
        setResultsTab('facts');
        setAnswer("");
        setDiagnosis("");
        setCollapsedGroups({});
        setExpandedCitations({});

        // The backend deletes the stale extraction fields (structuredInfo,
        // hpi, timeline, codes...) before it enqueues the run, so onSnapshot
        // cannot repopulate old values. It runs with the Admin SDK, which is
        // what lets a colleague re-extract a case they do not own (B4).

        // Track that we're awaiting fresh extraction results
        pendingExtraction.current = true;

        try {
            const formData = new FormData();
            formData.append('case_id', caseId);
            if (!isOwner) formData.append('owner_uid', ownerUid);

            await api.post('/case/process-case', formData);
        } catch (error) {
            if (isDenied(error)) {
                setAccessDenied(true);
                return;
            }
            console.error('Error starting extraction:', error);
            notify.error('Failed to start extraction. Please try again.');
            pendingExtraction.current = false;
        }
    };

    const handleGenerateAll = async ({ confirm = false } = {}) => {
        if (!user?.uid || !caseId || isArchived) return;
        if (identityBlocked) {
            notify.warning('Confirm the patient identity before generating');
            return;
        }
        if (!reviewGatePassed()) return;

        // If existing documents exist, confirm before wiping them
        const hasExistingDocs = !!(answer || diagnosis);
        if (hasExistingDocs && !confirm) {
            setShowReGenerateConfirm(true);
            return;
        }
        setShowReGenerateConfirm(false);

        // Clear existing documents locally; the backend deletes the stale
        // hpi/diagnosis fields before regeneration (B4).
        setAnswer("");
        setDiagnosis("");

        // Immediately reflect generating state in UI; Firestore will keep it updated
        setIsGenerating(true);
        try {
            const formData = new FormData();
            formData.append('case_id', caseId);
            if (!isOwner) formData.append('owner_uid', ownerUid);

            await api.post('/case/generate_initial_content', formData);
        } catch (error) {
            if (isDenied(error)) {
                setAccessDenied(true);
                return;
            }
            // F1 (R2): the backend enforces the identity gate too. A 409 with
            // code identity_unresolved gets the same toast as the local gate.
            if (error?.response?.status === 409 && error.response.data?.code === 'identity_unresolved') {
                notify.warning('Confirm the patient identity before generating');
                setIsGenerating(false);
                return;
            }
            console.error('Error generating content:', error);
            const errorMessage = error.response?.data?.details || error.message;
            notify.error(`Failed to generate content: ${errorMessage}`);
            setIsGenerating(false);
        }

        // Codes are suggested at upload time; Generate deliberately does NOT
        // re-suggest, since that would overwrite the coordinator's code
        // review. requestCodeSuggestion below backs the manual re-suggest
        // button in the Coding step.
    };

    // F2: re-run only the steps with unresolved warnings. The banner keeps
    // its button disabled until this settles; a rejection re-enables it.
    const handleRetryFailedSteps = async () => {
        if (!user?.uid || !caseId || isArchived) return;
        try {
            const formData = new FormData();
            formData.append('case_id', caseId);
            if (!isOwner) formData.append('owner_uid', ownerUid);
            await api.post('/case/retry-failed', formData);
            notify.success('Retrying failed steps');
        } catch (error) {
            const status = error?.response?.status;
            const serverError = error?.response?.data?.error;
            const serverCode = error?.response?.data?.code;
            if (isBillingBlockedError(error)) {
                // BillingBlockedModal (api.js) already shows the billing message.
            } else if (isDenied(error)) {
                notify.error('You do not have access to this case.');
            } else if (status === 409 && serverCode === 'case_busy') {
                notify.error('A retry is already running');
            } else if (status === 409) {
                notify.error('Nothing to retry');
            } else {
                console.error('Error retrying failed steps:', error);
                notify.error(typeof serverError === 'string' && serverError
                    ? serverError
                    : 'Failed to retry the failed steps');
            }
            throw error;
        }
    };

    const requestCodeSuggestion = async () => {
        if (!user?.uid || !caseId || isArchived) return;
        try {
            const formData = new FormData();
            formData.append('case_id', caseId);
            if (!isOwner) formData.append('owner_uid', ownerUid);
            await api.post('/case/suggest-diagnosis-codes', formData);
        } catch (error) {
            if (isBillingBlockedError(error)) return; // BillingBlockedModal already open
            if (isDenied(error)) {
                setAccessDenied(true);
                return;
            }
            console.error('Error requesting code suggestion:', error);
            notify.error('Failed to start code suggestion. Please try again.');
        }
    };

    const { change: changeAdmissionStatus } = useUpdateAdmissionStatus({
        caseId,
        currentStatus: admissionStatus,
        onOptimisticChange: setAdmissionStatus,
        ownerUid: isOwner ? undefined : ownerUid,
    });

    const handleAdmissionStatusChange = (e) => changeAdmissionStatus(e.target.value);

    const handleFactVerification = async (field, isCorrect, e) => {
        e.preventDefault();

        if (!user?.uid || !caseId) return;

        // If HPI exists and a fact changes, downstream documents are stale
        if (answer) setDocumentsStale(true);

        const prevVerified = structuredInfo?.[field]?.verified;
        const prevReview = structuredInfo?.[field]?.review;
        const stamp = reviewStamp(reviewerOf(user, userProfile));
        const generation = caseGenerationRef.current;
        try {
            // Update local state immediately for better UX
            setStructuredInfo(prev => ({
                ...prev,
                [field]: {
                    ...prev[field],
                    verified: isCorrect,
                    review: stamp,
                }
            }));

            // Update Firebase
            await updateDoc(caseRef, ...factReviewArgs(field, isCorrect, stamp));
            if (!stillOnCase(generation)) return;

            // If "Modify" is clicked or "Correct" is unchecked (isCorrect is false), make the field editable
            if (!isCorrect) {
                setEditableAnswers(prev => ({
                    ...prev,
                    [field]: structuredInfo[field].answer
                }));
            } else {
                // If "Correct" is clicked, remove the field from editableAnswers
                setEditableAnswers(prev => {
                    const newState = { ...prev };
                    delete newState[field];
                    return newState;
                });
            }
        } catch (error) {
            console.error('Error updating verification state:', error);
            notify.error('Could not save the fact review. Try again.');
            if (!stillOnCase(generation)) return;
            // Revert local state if Firebase update fails, unless a newer
            // click has already replaced the value this call set.
            setStructuredInfo(prev => (
                prev?.[field]?.review === stamp
                    ? { ...prev, [field]: { ...prev[field], verified: prevVerified, review: prevReview } }
                    : prev
            ));
        }
    };

    const handleRedFlagReview = async (index, newStatus) => {
        if (!user?.uid || !caseId || !redFlags) return;

        const prevFlags = redFlags.flags;
        // Toggle: a click on the active status returns the flag to pending.
        const updatedFlags = reviewRedFlag(prevFlags, index, newStatus, reviewStamp(reviewerOf(user, userProfile)));

        setRedFlags(prev => ({ ...prev, flags: updatedFlags }));

        const generation = caseGenerationRef.current;
        try {
            await updateDoc(caseRef, {
                'redFlags.flags': updatedFlags,
                last_modified_date: serverTimestamp()
            });
        } catch (error) {
            console.error('Error updating red flag status:', error);
            notify.error('Could not save the red flag review. Try again.');
            if (!stillOnCase(generation)) return;
            setRedFlags(prev => ({ ...prev, flags: prevFlags }));
        }
    };

    const handleMarkAllRedFlagsReviewed = async () => {
        if (!user?.uid || !caseId || !redFlags) return;

        const prevFlags = redFlags.flags;
        const updatedFlags = markAllRedFlagsReviewed(
            prevFlags, reviewStamp(reviewerOf(user, userProfile), { bulk: true }),
        );

        setRedFlags(prev => ({ ...prev, flags: updatedFlags }));

        const generation = caseGenerationRef.current;
        try {
            await updateDoc(caseRef, {
                'redFlags.flags': updatedFlags,
                last_modified_date: serverTimestamp()
            });
        } catch (error) {
            console.error('Error marking all red flags reviewed:', error);
            notify.error('Could not mark the red flags reviewed. Try again.');
            if (!stillOnCase(generation)) return;
            setRedFlags(prev => ({ ...prev, flags: prevFlags }));
        }
    };

    const handleDiagnosisCodeReview = async (index, newStatus) => {
        if (!user?.uid || !caseId || !diagnosisCodes) return;

        const prevCandidates = diagnosisCodes.candidates;
        const prevPrimary = diagnosisCodes.primary_index ?? null;
        const prevPrimaryReview = diagnosisCodes.primary_review ?? null;
        // Toggle, and a candidate leaving 'selected' loses any primary designation.
        const { candidates: updatedCandidates, primaryIndex: updatedPrimary, primaryCleared } =
            reviewCandidate(prevCandidates, prevPrimary, index, newStatus, reviewStamp(reviewerOf(user, userProfile)));
        const primaryReviewUpdate = primaryCleared ? { primary_review: null } : {};

        setDiagnosisCodes(prev => ({
            ...prev, candidates: updatedCandidates, primary_index: updatedPrimary, ...primaryReviewUpdate,
        }));

        const generation = caseGenerationRef.current;
        try {
            await updateDoc(caseRef, {
                'diagnosisCodes.candidates': updatedCandidates,
                'diagnosisCodes.primary_index': updatedPrimary,
                ...(primaryCleared ? { 'diagnosisCodes.primary_review': null } : {}),
                last_modified_date: serverTimestamp(),
            });
        } catch (error) {
            console.error('Error updating diagnosis code status:', error);
            notify.error('Could not save the code review. Try again.');
            if (!stillOnCase(generation)) return;
            setDiagnosisCodes(prev => ({
                ...prev, candidates: prevCandidates, primary_index: prevPrimary, primary_review: prevPrimaryReview,
            }));
        }
    };

    const handleDesignatePrimary = async (index) => {
        if (!user?.uid || !caseId || !diagnosisCodes) return;

        const prevPrimary = diagnosisCodes.primary_index ?? null;
        const prevPrimaryReview = diagnosisCodes.primary_review ?? null;
        const stamp = reviewStamp(reviewerOf(user, userProfile));
        setDiagnosisCodes(prev => ({ ...prev, primary_index: index, primary_review: stamp }));

        const generation = caseGenerationRef.current;
        try {
            await updateDoc(caseRef, {
                'diagnosisCodes.primary_index': index,
                'diagnosisCodes.primary_review': stamp,
                last_modified_date: serverTimestamp(),
            });
        } catch (error) {
            console.error('Error designating primary diagnosis:', error);
            notify.error('Could not set the primary diagnosis. Try again.');
            if (!stillOnCase(generation)) return;
            setDiagnosisCodes(prev => ({ ...prev, primary_index: prevPrimary, primary_review: prevPrimaryReview }));
        }
    };

    const handleCopyGeneratedDoc = async (docName) => {
        const content = generatedDocuments?.[docName]?.content;
        if (!content) return;
        const copied = await copyWithProvenance(content, {
            generatedAt: generatedDocuments?.[docName]?.generated_at,
            signoff: reviewSignoff,
        });
        if (!copied) return;
        setCopiedDocName(docName);
        setTimeout(() => setCopiedDocName(null), 2000);
    };

    const handleCopyDiagnosisCodes = async () => {
        if (!diagnosisCodes) return;
        const { candidates, primary_index: primaryIndex } = diagnosisCodes;
        const selected = candidates
            .map((cand, i) => ({ cand, i }))
            .filter(({ cand }) => cand.code && cand.status === 'selected');
        // Primary first, then remaining selected codes in list order
        selected.sort((a, b) => (a.i === primaryIndex ? -1 : b.i === primaryIndex ? 1 : 0));
        const text = selected.map(({ cand }) => cand.code).join('\n');
        const copied = await copyWithProvenance(text, {
            generatedAt: lastModifiedAt,
            signoff: reviewSignoff,
        });
        if (!copied) return;
        setCopyDxCodesSuccess(true);
        setTimeout(() => setCopyDxCodesSuccess(false), 2000);
    };

    const handleCopyPhysicianQuery = async (index) => {
        const candidate = diagnosisCodes?.candidates?.[index];
        if (!candidate) return;
        const first = structuredInfo?.['Patient First Name']?.answer;
        const last = structuredInfo?.['Patient Last Name']?.answer;
        const patientName = [first, last].filter(Boolean).join(' ');
        const text = buildPhysicianQuery(candidate, patientName ? { patientName } : {});
        const copied = await copyWithProvenance(text, {
            generatedAt: lastModifiedAt,
            signoff: reviewSignoff,
        });
        if (!copied) return;
        setCopiedQueryIndex(index);
        setTimeout(() => setCopiedQueryIndex(null), 2000);
    };

    /* ── User-driven code search / refine / evidence lookup ── */

    // Server evidence results land at diagnosisCodes.userEvidence.{client_id}
    // (disjoint from the client-owned candidates array); overlay them onto
    // the matching candidates at render time.
    const mergedDiagnosisCodes = useMemo(() => {
        if (!diagnosisCodes) return null;
        const userEvidence = diagnosisCodes.userEvidence || {};
        const candidates = (diagnosisCodes.candidates || []).map(cand => {
            if (!cand.client_id) return cand;
            const result = userEvidence[cand.client_id];
            if (!result) {
                return evidenceTimeouts[cand.client_id]
                    ? { ...cand, evidence_status: 'timeout' }
                    : cand;
            }
            if (result.status !== 'found') return { ...cand, evidence_status: result.status };
            return {
                ...cand,
                evidence_status: 'found',
                evidence: result.evidence,
                page: result.page,
                document: result.document,
            };
        });
        return { ...diagnosisCodes, candidates };
    }, [diagnosisCodes, evidenceTimeouts]);

    // Evidence results for lookup-mode checks, keyed by code. Lookup checks
    // reuse the userEvidence mechanism with a lookup- prefixed client_id and
    // no candidate row, so the panel merge never sees them.
    const lookupEvidence = useMemo(() => {
        const userEvidence = diagnosisCodes?.userEvidence || {};
        return Object.fromEntries(
            Object.entries(lookupClientIds).map(([code, clientId]) => {
                const result = userEvidence[clientId];
                if (result) return [code, result];
                return [code, { status: evidenceTimeouts[clientId] ? 'timeout' : 'pending' }];
            })
        );
    }, [lookupClientIds, diagnosisCodes, evidenceTimeouts]);

    const startEvidenceClock = useCallback((clientId) => {
        if (evidenceTimersRef.current[clientId]) {
            clearTimeout(evidenceTimersRef.current[clientId]);
        }
        evidenceTimersRef.current[clientId] = setTimeout(() => {
            delete evidenceTimersRef.current[clientId];
            setEvidenceTimeouts(prev => ({ ...prev, [clientId]: true }));
        }, 90000);
    }, []);

    // Clear timeout clocks (and flags) for candidates whose evidence arrived,
    // and start a clock for orphaned pendings — a persisted 'pending' with no
    // userEvidence entry and no running clock means the page was reloaded (or
    // the background task died) mid-search; without this it would spin forever
    // with no path to Retry.
    useEffect(() => {
        const userEvidence = diagnosisCodes?.userEvidence || {};
        Object.keys(userEvidence).forEach(clientId => {
            if (evidenceTimersRef.current[clientId]) {
                clearTimeout(evidenceTimersRef.current[clientId]);
                delete evidenceTimersRef.current[clientId];
            }
        });
        setEvidenceTimeouts(prev => {
            const next = Object.fromEntries(
                Object.entries(prev).filter(([clientId]) => !userEvidence[clientId])
            );
            return Object.keys(next).length === Object.keys(prev).length ? prev : next;
        });
        (diagnosisCodes?.candidates || []).forEach(cand => {
            if (
                cand.client_id &&
                cand.evidence_status === 'pending' &&
                !userEvidence[cand.client_id] &&
                !evidenceTimersRef.current[cand.client_id] &&
                !evidenceTimeouts[cand.client_id]
            ) {
                startEvidenceClock(cand.client_id);
            }
        });
    }, [diagnosisCodes, evidenceTimeouts, startEvidenceClock]);

    useEffect(() => () => {
        Object.values(evidenceTimersRef.current).forEach(clearTimeout);
    }, []);

    const fireEvidenceSearch = (code, clientId) => {
        setEvidenceTimeouts(prev => {
            if (!prev[clientId]) return prev;
            const { [clientId]: _removed, ...rest } = prev;
            return rest;
        });
        const formData = new FormData();
        formData.append('case_id', caseId);
        if (!isOwner) formData.append('owner_uid', ownerUid);
        formData.append('code', code);
        formData.append('client_id', clientId);
        api.post('/case/find-code-evidence', formData).catch(error => {
            if (isDenied(error)) {
                setAccessDenied(true);
                return;
            }
            console.error('Error starting evidence search:', error);
            setEvidenceTimeouts(prev => ({ ...prev, [clientId]: true }));
        });
        startEvidenceClock(clientId);
    };

    // Table facts arrive with the search/browse hit, so the candidate is
    // stamped client-side with no extra roundtrip.
    const buildUserCandidate = (hit, clientId) => ({
        code: hit.code,
        rationale: '',
        evidence: '',
        page: '',
        document: '',
        missing_fact: null,
        validation: {
            valid: hit.billable,
            description: hit.description,
            acceptable_primary: hit.pdgm?.acceptable_primary ?? false,
            clinical_group: hit.pdgm?.clinical_group ?? null,
            comorbidity_subgroup: hit.pdgm?.comorbidity_subgroup ?? null,
        },
        verification: null,
        audit_flags: [],
        query_options: [],
        status: 'selected',
        source: 'user',
        client_id: clientId,
        evidence_status: 'pending',
    });

    const fetchExcludes1Flags = async (newCode) => {
        const selectedCodes = diagnosisCodes.candidates
            .filter(c => c.code && c.status === 'selected' && c.code !== newCode)
            .map(c => c.code);
        if (selectedCodes.length === 0) return [];
        try {
            // Same fiscal-year table as the code search: an Excludes1 note
            // can change between years, so the check follows the case date.
            const params = { codes: [...selectedCodes, newCode].join(',') };
            if (caseAsOf) params.as_of = caseAsOf;
            const resp = await api.get('/icd/excludes1-conflicts', { params });
            return resp.data.conflicts
                .filter(c => c.code === newCode || c.conflicts_with === newCode)
                .map(c => `Excludes1 conflict: ${c.code} excludes ${c.conflicts_with} (${c.note})`);
        } catch (error) {
            console.error('Error checking Excludes1 conflicts:', error);
            notify.warning('Could not check Excludes1 conflicts for this code.');
            return [];
        }
    };

    const handleCodeSearchSelect = async (hit) => {
        const target = codeSearchTarget;
        setCodeSearchTarget(null);
        if (!user?.uid || !caseId || !diagnosisCodes || !target) return;
        if (target.mode === 'refine' && diagnosisCodes.candidates[target.index]?.code === hit.code) {
            return; // picked the same code — nothing to change
        }
        // A code already on the list (and not dismissed) must not be added
        // twice — the copy list would emit it twice.
        const duplicate = diagnosisCodes.candidates.some(
            (cand, i) =>
                cand.code === hit.code &&
                cand.status !== 'rejected' &&
                !(target.mode === 'refine' && i === target.index)
        );
        if (duplicate) {
            notify.info(`${hit.code} is already on the code list.`);
            return;
        }

        // A lookup evidence check may already have run (or be running) for
        // this code; reusing its client_id lets the merge pick up that result
        // with no second search. Guarded against a dismissed candidate that
        // still carries the id from an earlier add.
        const lookupClientId = target.mode === 'lookup' ? lookupClientIds[hit.code] : null;
        const reuseLookupId = Boolean(
            lookupClientId &&
            !diagnosisCodes.candidates.some(c => c.client_id === lookupClientId)
        );
        const clientId = reuseLookupId ? lookupClientId : crypto.randomUUID();
        const newCandidate = buildUserCandidate(hit, clientId);
        const generation = caseGenerationRef.current;
        newCandidate.audit_flags = await fetchExcludes1Flags(hit.code);
        if (!stillOnCase(generation)) return;

        const prevCandidates = diagnosisCodes.candidates;
        const prevPrimary = diagnosisCodes.primary_index ?? null;
        const prevPrimaryReview = diagnosisCodes.primary_review ?? null;
        const stamp = reviewStamp(reviewerOf(user, userProfile));
        let updatedCandidates;
        let updatedPrimary = prevPrimary;
        let primaryReviewUpdate = {};
        if (target.mode === 'refine') {
            newCandidate.refined_from = diagnosisCodes.candidates[target.index]?.code ?? null;
            const refined = refineCandidate(prevCandidates, prevPrimary, target.index, newCandidate, stamp);
            updatedCandidates = refined.candidates;
            updatedPrimary = refined.primaryIndex;
            if (refined.primaryMoved) primaryReviewUpdate = { primary_review: stamp };
        } else {
            updatedCandidates = [...prevCandidates, { ...newCandidate, review: stamp }];
        }

        setDiagnosisCodes(prev => ({
            ...prev, candidates: updatedCandidates, primary_index: updatedPrimary, ...primaryReviewUpdate,
        }));
        try {
            await updateDoc(caseRef, {
                'diagnosisCodes.candidates': updatedCandidates,
                'diagnosisCodes.primary_index': updatedPrimary,
                ...(primaryReviewUpdate.primary_review ? { 'diagnosisCodes.primary_review': stamp } : {}),
                last_modified_date: serverTimestamp(),
            });
        } catch (error) {
            console.error('Error adding diagnosis code:', error);
            notify.error('Could not add the code. Try again.');
            if (!stillOnCase(generation)) return;
            setDiagnosisCodes(prev => ({
                ...prev, candidates: prevCandidates, primary_index: prevPrimary, primary_review: prevPrimaryReview,
            }));
            return;
        }
        if (!stillOnCase(generation)) return;
        if (!reuseLookupId) fireEvidenceSearch(hit.code, clientId);
    };

    const handleRefineCode = (index) => {
        const code = diagnosisCodes?.candidates?.[index]?.code;
        if (code) setCodeSearchTarget({ mode: 'refine', code, index });
    };

    const handleRetryEvidence = (index) => {
        const cand = diagnosisCodes?.candidates?.[index];
        if (cand?.code && cand?.client_id) fireEvidenceSearch(cand.code, cand.client_id);
    };

    const handleLookupEvidence = (code) => {
        const existing = lookupClientIds[code];
        if (existing) {
            // Retry path: same id, so the result overwrites in place.
            fireEvidenceSearch(code, existing);
            return;
        }
        const clientId = `lookup-${crypto.randomUUID()}`;
        setLookupClientIds(prev => ({ ...prev, [code]: clientId }));
        fireEvidenceSearch(code, clientId);
    };

    const handleAnswerEdit = async (field, newValue) => {
        if (!user?.uid || !caseId) return;

        // If HPI exists and a fact changes, downstream documents are stale
        if (answer) setDocumentsStale(true);

        const generation = caseGenerationRef.current;
        const seq = (answerSaveSeqRef.current[field] || 0) + 1;
        answerSaveSeqRef.current[field] = seq;
        try {
            setEditableAnswers(prev => ({ ...prev, [field]: newValue }));
            setStructuredInfo(prev => ({
                ...prev,
                [field]: { ...prev[field], answer: newValue }
            }));

            await updateDoc(caseRef, ...factUpdateArgs(field, 'answer', newValue));
            if (!stillOnCase(generation)) return;
            if (seq >= (answerConfirmedSeqRef.current[field] || 0)) {
                answerConfirmedSeqRef.current[field] = seq;
                confirmedAnswersRef.current[field] = newValue;
            }
            failedEditFieldsRef.current.delete(field);
        } catch (error) {
            console.error('Error updating answer:', error);
            if (!stillOnCase(generation)) {
                notify.error('Could not save the edit. Try again.');
                return;
            }
            if (!failedEditFieldsRef.current.has(field)) {
                failedEditFieldsRef.current.add(field);
                notify.error('Could not save the edit. Try again.');
            }
            // Keystrokes fire overlapping saves: revert only if no newer
            // keystroke has replaced the value that failed, and revert to
            // the answer Firestore last held, not an earlier unsaved one.
            const confirmedAnswer = confirmedAnswersRef.current[field];
            setEditableAnswers(prev => {
                if (prev[field] !== newValue) return prev;
                const next = { ...prev };
                delete next[field];
                return next;
            });
            setStructuredInfo(prev => (
                prev?.[field]?.answer === newValue
                    ? { ...prev, [field]: { ...prev[field], answer: confirmedAnswer } }
                    : prev
            ));
        }
    };

    // Calculate verification stats
    const getVerificationStats = () => {
        if (!structuredInfo) return null;

        const totalFacts = Object.keys(structuredInfo).length;
        const verifiedCorrect = Object.values(structuredInfo).filter(v => v.verified === true).length;

        return {
            total: totalFacts,
            verified: verifiedCorrect
        };
    };

    // Check if all facts are verified correct
    const allFactsVerifiedCorrect = () => {
        const stats = getVerificationStats();
        return stats?.verified === stats?.total && stats?.total > 0;
    };

    // Patient identity (F1): the backend synthesizes one Critical flag with
    // a fixed id when the uploaded documents disagree on the patient. It
    // blocks the gate while pending or approved; a dismissal (status
    // rejected, "same patient") opens it. Status writes reuse
    // handleRedFlagReview by index.
    const identityFlagIndex = redFlags?.flags?.findIndex(f => f?.id === 'patient_identity_mismatch') ?? -1;
    const identityFlag = identityFlagIndex >= 0 ? redFlags.flags[identityFlagIndex] : null;
    const identityBlocked = Boolean(identityFlag) && identityFlag.status !== 'rejected';

    // The full review gate: verified facts plus every active timeline event
    // confirmed or dismissed, and no open patient identity flag. Cases
    // without active events behave as before.
    const reviewGatePassed = () =>
        allFactsVerifiedCorrect()
        && allActiveEventsReviewed(timeline, timelineReview)
        && !identityBlocked;

    // F6: the gate passing is the sign-off. Record who closed it, and clear
    // the record when the gate reopens (a fact un-verified, an event
    // unreviewed, an identity flag raised). The write carries no state
    // update of its own; onSnapshot echoes the result back.
    //
    // Only a transition seen on this page records a name. Opening a case
    // that is already fully verified proves nothing about who reviewed it,
    // so the first snapshot sets the baseline and writes nothing; such a
    // case reads "Review pending" until someone re-verifies something.
    // Clearing is not attribution, so it still runs whenever the gate
    // reopens, whoever is looking.
    const gatePassed = reviewGatePassed();
    useEffect(() => {
        if (!caseLoaded) return;
        if (!caseRef || !user?.uid || !caseId || isArchived || accessDenied) return;

        const previousGate = prevGateRef.current;
        prevGateRef.current = gatePassed;
        if (previousGate === null) return;

        const hasSignoff = Boolean(reviewSignoff);
        if (gatePassed === hasSignoff) {
            signoffIntentRef.current = null;
            return;
        }
        // A gate that was already passing when this page saw it never
        // records a sign-off, however the sign-off came to be missing.
        if (gatePassed && previousGate) return;
        // One write per gate change: the echo clears the intent above.
        if (signoffIntentRef.current === gatePassed) return;
        signoffIntentRef.current = gatePassed;

        const payload = gatePassed
            ? {
                review_signoff: {
                    name: userProfile?.name || user?.displayName || user?.email || 'Unknown',
                    uid: user.uid,
                    at: serverTimestamp(),
                },
                last_modified_date: serverTimestamp(),
            }
            : { review_signoff: deleteField(), last_modified_date: serverTimestamp() };

        const generation = caseGenerationRef.current;
        updateDoc(caseRef, payload).catch((error) => {
            console.error('Error recording review sign-off:', error);
            notify.error('Could not record the review sign-off.');
            // Clear the intent so the next run of this effect retries.
            if (stillOnCase(generation)) signoffIntentRef.current = null;
        });
    }, [caseLoaded, gatePassed, reviewSignoff, caseRef, caseId, isArchived,
        accessDenied, user, userProfile]);

    // Bump the operation counter of each event a timeline review changes;
    // returns the counters this operation owns.
    const beginTimelineOps = (eventIds) => {
        const ops = {};
        eventIds.forEach((id) => {
            ops[id] = (timelineOpRef.current[id] || 0) + 1;
            timelineOpRef.current[id] = ops[id];
        });
        return ops;
    };

    // Restore each event a failed review changed to its saved entry, unless
    // a newer review of that event owns it now.
    const rollbackTimelineOps = (ops) => {
        const owned = Object.keys(ops).filter((id) => timelineOpRef.current[id] === ops[id]);
        if (owned.length === 0) return;
        setTimelineReview(prev => {
            const next = { ...prev };
            owned.forEach((id) => {
                const saved = confirmedTimelineRef.current[id];
                if (saved === undefined) delete next[id];
                else next[id] = saved;
            });
            return next;
        });
    };

    const handleTimelineReview = async (eventId, decision, correctedStatus = null) => {
        if (!user?.uid || !caseId || isArchived) return;
        if (answer) setDocumentsStale(true);

        // Optimistic local update; onSnapshot confirms
        const generation = caseGenerationRef.current;
        const stamp = reviewStamp(reviewerOf(user, userProfile));
        const entry = decision ? reviewEntry(decision, correctedStatus, stamp) : undefined;
        const op = beginTimelineOps([eventId])[eventId];
        setTimelineReview(prev => {
            const next = { ...prev };
            if (!entry) {
                delete next[eventId];
            } else {
                next[eventId] = entry;
            }
            return next;
        });

        try {
            await updateDoc(caseRef, ...eventReviewArgs(eventId, decision, correctedStatus, stamp));
            if (!stillOnCase(generation)) return;
            confirmedTimelineRef.current[eventId] = entry;
        } catch (error) {
            console.error('Error saving timeline review:', error);
            notify.error('Could not save the timeline review. Try again.');
            if (!stillOnCase(generation)) return;
            rollbackTimelineOps({ [eventId]: op });
        }
    };

    const handleConfirmAllTimeline = async (events) => {
        if (!user?.uid || !caseId || isArchived) return;
        const stamp = reviewStamp(reviewerOf(user, userProfile), { bulk: true });
        const args = confirmAllArgs(events, timelineReview, stamp);
        if (!args) return;

        const generation = caseGenerationRef.current;
        const confirmedIds = (events || [])
            .filter((e) => e?.id && !timelineReview?.[e.id]?.decision)
            .map((e) => e.id);
        const ops = beginTimelineOps(confirmedIds);
        const entries = {};
        confirmedIds.forEach((id) => {
            entries[id] = reviewEntry('confirmed', null, stamp);
        });
        setTimelineReview(prev => ({ ...prev, ...entries }));

        try {
            await updateDoc(caseRef, ...args);
            if (!stillOnCase(generation)) return;
            Object.assign(confirmedTimelineRef.current, entries);
        } catch (error) {
            console.error('Error bulk-confirming timeline events:', error);
            notify.error('Could not confirm the timeline events. Try again.');
            if (!stillOnCase(generation)) return;
            rollbackTimelineOps(ops);
        }
    };

    const handleDismissFactReviewBanner = async () => {
        try {
            await api.put('/user/profile', { fact_review_onboarding_dismissed: true });
        } catch (error) {
            console.error('Failed to dismiss fact-review banner:', error);
            notify.error('Could not save your preference.');
        }
    };

    const handleMarkAllCorrect = async () => {
        if (!user?.uid || !caseId || !structuredInfo) return;

        const prevInfo = structuredInfo;
        const prevEditable = editableAnswers;
        const prevCollapsed = collapsedGroups;
        const generation = caseGenerationRef.current;
        try {
            const stamp = reviewStamp(reviewerOf(user, userProfile), { bulk: true });
            // Update local state for immediate feedback
            setStructuredInfo(prev => {
                if (!prev) return prev;
                const updated = {};
                // Already verified facts keep their reviewer (markAllVerifiedArgs).
                Object.entries(prev).forEach(([key, value]) => {
                    updated[key] = value?.verified === true ? value : { ...value, verified: true, review: stamp };
                });
                return updated;
            });

            // Clear edits since all are marked correct
            setEditableAnswers({});

            // Collapse all groups
            const collapsed = {};
            groupedFacts.forEach(g => { collapsed[g.name] = true; });
            setCollapsedGroups(collapsed);

            // Single varargs update; FieldPath segments keep admin-authored
            // fact names with reserved characters safe (see factPaths.js).
            await updateDoc(caseRef, ...markAllVerifiedArgs(structuredInfo, stamp));
            if (!stillOnCase(generation)) return;

            // Bulk = everything: the admin escape hatch also confirms all
            // unreviewed timeline events so the gate opens in one click
            await handleConfirmAllTimeline(timeline);
        } catch (error) {
            console.error('Error marking all as correct:', error);
            notify.error('Could not mark all facts correct. Try again.');
            if (!stillOnCase(generation)) return;
            setStructuredInfo(prevInfo);
            setEditableAnswers(prevEditable);
            setCollapsedGroups(prevCollapsed);
        }
    };

    const handleCopyHPI = async () => {
        // The clipboard write and its textarea fallback live in
        // utils/provenance so every copy path appends the same footer.
        const copied = await copyWithProvenance(answer, {
            generatedAt: hpiGeneratedAt,
            signoff: reviewSignoff,
        });
        setCopySuccess(copied ? "Copied!" : "Failed to copy!");
        setTimeout(() => {
            setCopySuccess("");
        }, 2000);
    };

    const handleCopyDiagnosis = async () => {
        const copied = await copyWithProvenance(diagnosis, {
            generatedAt: diagnosisGeneratedAt,
            signoff: reviewSignoff,
        });
        setCopyDiagnosisSuccess(copied ? "Copied!" : "Failed to copy!");
        setTimeout(() => {
            setCopyDiagnosisSuccess("");
        }, 2000);
    };


    const toggleFileName = (key) => {
        setExpandedFileNames(prev => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    };

    // Upload section used in Stage 1
    const uploadSection = (
        <div className="form">
            {uploadedFiles.length > 0 && (
                <div className="uploaded-files" id="case-files">
                    <h3>Uploaded Files</h3>
                    <ul>
                        {uploadedFiles.map((file, index) => (
                            <li key={file.path || index} className="uploaded-file-item">
                                <span
                                    className={`file-name${expandedFileNames.has(file.path || index) ? ' expanded' : ''}`}
                                    title={file.name}
                                    onClick={() => toggleFileName(file.path || index)}
                                >{file.name}</span>
                                <span className="file-date">
                                    {new Date(file?.uploadedAt?.toDate ? file.uploadedAt.toDate() : file.uploadedAt).toLocaleString()}
                                </span>
                                {canRemoveFiles && (
                                    <button
                                        type="button"
                                        className="delete-file-button"
                                        onClick={() => handleDeleteFile(file)}
                                        disabled={removingFile}
                                    >
                                        Remove
                                    </button>
                                )}
                            </li>
                        ))}
                    </ul>
                    <ConfirmDialog
                        open={pendingRemoval !== null}
                        title="Remove this document?"
                        confirmLabel="Remove document"
                        danger
                        busy={removingFile}
                        onConfirm={confirmRemoveFile}
                        onCancel={cancelRemoveFile}
                    >
                        <p>{pendingRemoval?.name}</p>
                        <p>The extraction will need to run again.</p>
                    </ConfirmDialog>
                </div>
            )}

            {/* Uploading to a colleague's case would fork it through
                finalize_upload's create-if-missing fallback; only the owner
                adds or removes documents (B4 review, R2). */}
            {isOwner && (
                <>
                    <div {...getRootProps()} className="dropzone">
                        <input {...getInputProps()} />
                        {caseIsFull ? (
                            <p>{CASE_FULL_MESSAGE}</p>
                        ) : isDragActive ? (
                            <p>Drop files here ...</p>
                        ) : (
                            <p>Drag and drop PDF or DOCX files here, or click to select files</p>
                        )}
                        {selectedFiles.length > 0 && (
                            <ul className="selected-files-list">
                                {selectedFiles.map((f, i) => (
                                    <li key={i} className="selected-file">{f.name}</li>
                                ))}
                            </ul>
                        )}
                    </div>

                    <button
                        className="form-button"
                        type="button"
                        onClick={handleFileUpload}
                        disabled={!selectedFiles.length}
                    >
                        Upload Files
                    </button>
                </>
            )}
        </div>
    );

    // While UploadContext is actively tracking files for *this* case, show
    // progress instead of the dropzone — avoids a confusing empty dropzone
    // while uploads for this case are still in flight (or need
    // retry/removal) in the background.
    //
    // Non-terminal files (pending/uploading/stalled/failed) always keep the
    // panel up. But for an `autoProcess`-eligible batch (the NewCase flow —
    // uploads that continue in the background after navigating straight to
    // EditCase), we ALSO keep the panel up once every file reaches `done`
    // but before uploadCtx.clear() fires: that clear() only happens once
    // Firestore's onSnapshot reflects extraction underway, and there's a
    // real gap between "last file finalized" and that snapshot arriving.
    // Dropping to the dropzone/Extract UI during that gap let a user fire a
    // second /case/process-case by clicking the (still-enabled) Extract
    // button, racing the context's own auto-fired call.
    const hasRelevantFiles = uploadCtx.caseId === caseId &&
        uploadCtx.files.some((f) => f.status !== 'removed');
    const uploadInProgress = uploadCtx.files.some(
        (f) => ['pending', 'uploading', 'stalled', 'failed'].includes(f.status)
    );
    const showUploadPanel = hasRelevantFiles && (uploadInProgress || uploadCtx.autoProcess);
    // Defense in depth: even if showUploadPanel's window is somehow missed
    // (e.g. a remount), never allow the manual Extract button to fire a
    // second process-case for a batch UploadContext already auto-triggered.
    const uploadCtxAlreadyProcessing = uploadCtx.caseId === caseId && uploadCtx.processTriggered;

    const renderStage = () => {
        switch (activeStage) {
            /* ── Stage 1: Upload & Manage ── */
            case 'upload':
                return (
                    <div className="stage-single-column stage-upload">
                        <div className="workflow-hint">
                            <span className="workflow-hint-icon">ℹ️</span>
                            <span>Add or remove patient medical history files. You can upload multiple PDF or DOCX files. Once all files are uploaded, extract the medical information to continue.</span>
                        </div>
                        {showUploadPanel ? (
                            <UploadProgressPanel onStartExtraction={handleProcessCase} />
                        ) : (
                            uploadSection
                        )}

                        {resultsStale && (structuredInfo || extractionProgress) && (
                            <div className="stale-banner">
                                <div className="stale-banner-message">
                                    Source documents have changed. Extraction results and generated documents are out of date.
                                </div>
                                {showReExtractConfirm ? (
                                    <div className="confirm-reextract">
                                        <p className="confirm-reextract-warning">
                                            This will erase all existing extracted facts, verifications, and generated documents. They cannot be recovered.
                                        </p>
                                        <div className="confirm-reextract-actions">
                                            <button
                                                className="form-button form-button-danger"
                                                type="button"
                                                onClick={() => handleProcessCase({ confirm: true })}
                                            >
                                                Confirm Re-extraction
                                            </button>
                                            <button
                                                className="form-button form-button-secondary"
                                                type="button"
                                                onClick={() => setShowReExtractConfirm(false)}
                                            >
                                                Cancel
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <button
                                        className="form-button"
                                        type="button"
                                        onClick={handleProcessCase}
                                        disabled={isGenerating}
                                    >
                                        {isGenerating ? 'Extracting...' : 'Re-extract Medical Information'}
                                    </button>
                                )}
                            </div>
                        )}

                        {uploadedFiles.length > 0 && !structuredInfo && !extractionProgress && (
                            <button
                                className="form-button"
                                type="button"
                                onClick={handleProcessCase}
                                disabled={isGenerating || uploadCtxAlreadyProcessing}
                            >
                                {uploadCtxAlreadyProcessing ? 'Starting extraction…' : 'Extract Medical Information'}
                            </button>
                        )}
                    </div>
                );

            /* ── Stage 2: Extraction Results ── */
            case 'results':
                return (
                    <div className="stage-single-column stage-results">
                        <div className="workflow-hint">
                            <span className="workflow-hint-icon">ℹ️</span>
                            <span>Review each extracted fact and mark it as correct, or edit it if the value is wrong. All facts must be approved before generating documents.</span>
                        </div>
                        {structuredInfo && (
                            <VerificationBar
                                verificationStats={getVerificationStats()}
                                allVerified={reviewGatePassed()}
                                activeEventProgress={activeReviewProgress(timeline, timelineReview)}
                                onMarkAllCorrect={handleMarkAllCorrect}
                                onGenerateAll={handleGenerateAll}
                                isGenerating={isGenerating}
                                processingTimedOut={processingTimedOut}
                                hasExistingHPI={!!answer}
                                showReGenerateConfirm={showReGenerateConfirm}
                                onCancelReGenerate={() => setShowReGenerateConfirm(false)}
                                isAdmin={isAdmin}
                                factReviewOnboardingDismissedAt={userProfile?.fact_review_onboarding_dismissed_at}
                                onDismissFactReviewBanner={handleDismissFactReviewBanner}
                                identityBlocked={identityBlocked}
                            />
                        )}

                        <ProcessingWarnings
                            warnings={processingWarnings}
                            status={caseStatus}
                            canRetry={canRetry && !isArchived}
                            onRetry={handleRetryFailedSteps}
                        />

                        <IdentityMismatchBanner
                            flag={identityFlag}
                            onStatus={(status) => handleRedFlagReview(identityFlagIndex, status)}
                            onGoToFiles={handleGoToFiles}
                        />

                        <RiskScoreBanner
                            riskScore={riskScore}
                            recommendation={recommendation}
                        />

                        <RedFlagsAlert
                            redFlags={redFlags}
                            onRedFlagReview={handleRedFlagReview}
                            onMarkAllReviewed={handleMarkAllRedFlagsReviewed}
                        />

                        {/* Cases without an extracted timeline (the pre-timeline
                            backlog, or a failed extraction) keep the flat facts
                            view: no empty care-plan card, no empty Timeline tab. */}
                        {timeline.length > 0 ? (
                            <>
                                <ActiveCarePlanCard
                                    events={timeline}
                                    review={timelineReview}
                                    onReview={handleTimelineReview}
                                    onConfirmAll={handleConfirmAllTimeline}
                                />

                                <ResultsTabs currentTab={resultsTab} onTabChange={setResultsTab} />

                                {/* Tab content is conditionally rendered (not display:none): switching tabs resets local scroll. */}
                                {resultsTab === 'facts' && (
                                    <div
                                        role="tabpanel"
                                        id={panelId('facts')}
                                        aria-labelledby={tabId('facts')}
                                    >
                                        <FactsPanel
                                            groupedFacts={groupedFacts}
                                            collapsedGroups={collapsedGroups}
                                            onToggleGroup={toggleGroup}
                                            extractionProgress={extractionProgress}
                                            editableAnswers={editableAnswers}
                                            expandedCitations={expandedCitations}
                                            onToggleCitation={toggleCitation}
                                            onFactVerification={handleFactVerification}
                                            onAnswerEdit={handleAnswerEdit}
                                            fileCount={uploadedFiles?.length ?? 0}
                                        />
                                    </div>
                                )}

                                {resultsTab === 'timeline' && (
                                    <div
                                        role="tabpanel"
                                        id={panelId('timeline')}
                                        aria-labelledby={tabId('timeline')}
                                    >
                                        <TimelineTab
                                            events={timeline}
                                            review={timelineReview}
                                            onReview={handleTimelineReview}
                                            onConfirmAll={handleConfirmAllTimeline}
                                        />
                                    </div>
                                )}
                            </>
                        ) : (
                            <FactsPanel
                                groupedFacts={groupedFacts}
                                collapsedGroups={collapsedGroups}
                                onToggleGroup={toggleGroup}
                                extractionProgress={extractionProgress}
                                editableAnswers={editableAnswers}
                                expandedCitations={expandedCitations}
                                onToggleCitation={toggleCitation}
                                onFactVerification={handleFactVerification}
                                onAnswerEdit={handleAnswerEdit}
                                fileCount={uploadedFiles?.length ?? 0}
                            />
                        )}
                    </div>
                );

            /* ── Coding stage: diagnosis-code review (home_health only) ── */
            case 'coding':
                return (
                    <div className="stage-single-column stage-coding">
                        {codingStatus?.state === 'running' && (
                            <div className="workflow-hint">
                                <span className="workflow-hint-icon">⏳</span>
                                <span>Suggesting diagnosis codes from the verified case context…</span>
                            </div>
                        )}
                        {codingStatus?.state === 'error' && (
                            <div className="workflow-hint workflow-hint--error">
                                <span className="workflow-hint-icon">⚠️</span>
                                <span>Code suggestion failed.</span>
                                <button
                                    className="form-button form-button-secondary"
                                    type="button"
                                    onClick={requestCodeSuggestion}
                                >
                                    Retry
                                </button>
                            </div>
                        )}
                        {diagnosisCodes && codingStatus?.state !== 'running' && (
                            <div className="coding-resuggest">
                                {showReSuggestConfirm ? (
                                    <div className="confirm-reextract-panel">
                                        <p className="confirm-reextract-message">
                                            Re-suggesting replaces the current code list, including your accept/reject reviews and evidence lookups. They cannot be recovered.
                                        </p>
                                        <div className="confirm-reextract-actions">
                                            <button
                                                className="form-button form-button-danger"
                                                type="button"
                                                onClick={() => {
                                                    setShowReSuggestConfirm(false);
                                                    requestCodeSuggestion();
                                                }}
                                            >
                                                Confirm Re-suggest
                                            </button>
                                            <button
                                                className="form-button form-button-secondary"
                                                type="button"
                                                onClick={() => setShowReSuggestConfirm(false)}
                                            >
                                                Cancel
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <button
                                        className="form-button form-button-secondary"
                                        type="button"
                                        onClick={() => setShowReSuggestConfirm(true)}
                                    >
                                        Re-suggest codes from verified context
                                    </button>
                                )}
                            </div>
                        )}
                        <DiagnosisCodesPanel
                            diagnosisCodes={mergedDiagnosisCodes}
                            onCodeReview={handleDiagnosisCodeReview}
                            onDesignatePrimary={handleDesignatePrimary}
                            onCopyCodes={handleCopyDiagnosisCodes}
                            copySuccess={copyDxCodesSuccess}
                            onCopyQuery={handleCopyPhysicianQuery}
                            copiedQueryIndex={copiedQueryIndex}
                            onLookupCode={() => setCodeSearchTarget({ mode: 'lookup' })}
                            onRefine={handleRefineCode}
                            onRetryEvidence={handleRetryEvidence}
                        />
                        {codeSearchTarget && (
                            <CodeSearchModal
                                target={codeSearchTarget}
                                onSelect={handleCodeSearchSelect}
                                onClose={() => setCodeSearchTarget(null)}
                                onLookupEvidence={handleLookupEvidence}
                                lookupEvidence={lookupEvidence}
                                asOf={caseAsOf}
                                caseCodes={codeSearchTarget.mode === 'lookup'
                                    ? new Set((diagnosisCodes?.candidates || [])
                                        .filter(c => c.code && c.status !== 'rejected')
                                        .map(c => c.code))
                                    : undefined}
                            />
                        )}
                    </div>
                );

            /* ── Stage 3: Generated Documents ── */
            case 'documents':
                return (
                    <div className="stage-single-column stage-documents">
                        {documentsStale && (answer || diagnosis) && (
                            <div className="stale-banner">
                                <div className="stale-banner-message">
                                    Extracted facts have changed since these documents were generated. Regenerate documents from the Extraction Results tab to reflect the latest facts.
                                </div>
                            </div>
                        )}

                        {isArchived && (
                            <div className="archived-banner">
                                This case has been archived. PHI data has been removed.
                            </div>
                        )}

                        {/* Top controls: admission status */}
                        <div className="admission-controls">
                            <label htmlFor="admission-status" className="admission-label">Admission Status</label>
                            <select
                                id="admission-status"
                                className="admission-select"
                                value={admissionStatus || ''}
                                onChange={handleAdmissionStatusChange}
                                disabled={isArchived}
                            >
                                <option value="" disabled>Select status...</option>
                                <option value="In progress">In progress</option>
                                <option value="Submitted to Insurance">Submitted to Insurance</option>
                                <option value="Insurance Approved">Insurance Approved</option>
                                <option value="Insurance Denied">Insurance Denied</option>
                                <option value="Rejected">Rejected</option>
                                <option value="Admitted">Admitted</option>
                                <option value="Closed">Closed</option>
                            </select>
                        </div>

                        {/* Sub-tabs for generated documents */}
                        {generatedDocuments ? (
                            <>
                                <div className="tabs">
                                    {sortedDocNames.map((docName) => (
                                        <button
                                            key={docName}
                                            type="button"
                                            className={`tab ${activeTab === docName ? "active" : ""}`}
                                            onClick={() => setActiveTab(docName)}
                                        >
                                            {docName}
                                        </button>
                                    ))}
                                </div>
                                <div className="tab-content">
                                    {sortedDocNames.map((docName) => {
                                        const doc = generatedDocuments[docName];
                                        return activeTab === docName && (
                                            <div key={docName}>
                                                {docName === "Diagnosis" ? (
                                                    <DiagnosisContent
                                                        diagnosis={doc.content}
                                                        onCopy={() => handleCopyGeneratedDoc(docName)}
                                                        copySuccess={copiedDocName === docName}
                                                        generatedAt={doc.generated_at}
                                                        signoff={reviewSignoff}
                                                    />
                                                ) : (
                                                    <HPIContent
                                                        hpiText={doc.content}
                                                        onCopy={() => handleCopyGeneratedDoc(docName)}
                                                        copySuccess={copiedDocName === docName}
                                                        generatedAt={doc.generated_at}
                                                        signoff={reviewSignoff}
                                                    />
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </>
                        ) : (
                            <>
                                <div className="tabs">
                                    <button
                                        type="button"
                                        className={`tab ${activeTab === "hpi" ? "active" : ""}`}
                                        onClick={() => setActiveTab("hpi")}
                                    >
                                        HPI
                                    </button>
                                    <button
                                        type="button"
                                        className={`tab ${activeTab === "diagnosis" ? "active" : ""}`}
                                        onClick={() => setActiveTab("diagnosis")}
                                    >
                                        Diagnosis
                                    </button>
                                </div>
                                <div className="tab-content">
                                    {activeTab === "hpi" && (
                                        <HPIContent
                                            hpiText={answer}
                                            onCopy={handleCopyHPI}
                                            copySuccess={copySuccess}
                                            generatedAt={hpiGeneratedAt}
                                            signoff={reviewSignoff}
                                        />
                                    )}
                                    {activeTab === "diagnosis" && (
                                        <DiagnosisContent
                                            diagnosis={diagnosis}
                                            onCopy={handleCopyDiagnosis}
                                            copySuccess={copyDiagnosisSuccess}
                                            generatedAt={diagnosisGeneratedAt}
                                            signoff={reviewSignoff}
                                        />
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                );

            default:
                return null;
        }
    };

    // Build set of stages that should show as completed (green checkmark).
    // A stage is only green when its completion criteria are met.
    // Downstream stages lose green when upstream data changes.
    const completedStages = useMemo(() => {
        const stages = new Set();
        // Upload is done once extraction has started or completed
        if (structuredInfo || extractionProgress) stages.add('upload');
        // Results is done when all facts are verified AND files haven't changed since extraction
        if (reviewGatePassed() && !resultsStale) stages.add('results');
        // Documents is done when both HPI and diagnosis are generated and nothing upstream has changed
        if ((answer && diagnosis) && !documentsStale && !resultsStale) stages.add('documents');
        return stages;
    }, [structuredInfo, extractionProgress, answer, diagnosis, documentsStale, resultsStale, redFlags]); // eslint-disable-line react-hooks/exhaustive-deps

    if (accessDenied) {
        return (
            <div className="editcase-wrapper" data-owner={ownerUid}>
                <div className="case-access-denied" role="alert">
                    <h2>You do not have access to this case</h2>
                    <p>It belongs to another agency, or you are no longer a member of its agency.</p>
                    <Link to="/cases">Back to cases</Link>
                </div>
            </div>
        );
    }

    return (
        <div className="editcase-wrapper" data-owner={ownerUid}>
            <WorkflowStepper
                currentStage={activeStage}
                progressStage={workflowStage}
                onStageClick={handleStageClick}
                completedStages={completedStages}
                showCodingStep={showCodingStep}
                reachableStages={reachableStages}
            />
            {renderStage()}
        </div>
    );
}

export default EditCase;
